import React, { useState, useEffect, Suspense, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Environment, ContactShadows } from '@react-three/drei';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { useLoader } from '@react-three/fiber';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import './App.css';

const PART_COLORS = {
  main: "#64748b",           // Slate
  single_probe: "#38bdf8",   // Sky Blue
  double_probe: "#818cf8",   // Indigo
  cable: "#f472b6",          // Pink
  double_cable: "#a855f7",   // Purple
  horizontal_probe: "#fbbf24" // Amber
};

// Component to load and display the STL
const ConfigModel = ({
  name,
  url,
  color = "#38bdf8",
  onLoaded
}) => {
  const geometry = useLoader(STLLoader, url);
  const meshRef = useRef();

  useEffect(() => {
    if (meshRef.current) {
      const box = new THREE.Box3().setFromObject(meshRef.current);
      if (onLoaded) {
        onLoaded(name, box);
      }
    }
  }, [geometry, onLoaded, name]);

  return (
    <mesh ref={meshRef} geometry={geometry} rotation={[-Math.PI / 2, 0, -Math.PI / 4]} castShadow receiveShadow>
      <meshStandardMaterial
        color={color}
        metalness={0.7}
        roughness={0.2}
        emissive={color}
        emissiveIntensity={0.05}
      />
    </mesh>
  );
};

// Camera controls overlay
const CameraOverlay = ({ controlsRef, onBack, initialDist }) => {
  const [zoomLevel, setZoomLevel] = useState(1);

  const handleZoom = (direction) => {
    if (!controlsRef.current) return;
    const factor = direction === 'in' ? 0.9 : 1.1;
    const camera = controlsRef.current.object;
    camera.position.multiplyScalar(factor);
    controlsRef.current.update();
  };

  const handleRotate = (direction) => {
    if (!controlsRef.current) return;

    const angle = Math.PI / 16;
    const camera = controlsRef.current.object;
    const offset = new THREE.Vector3();
    offset.copy(camera.position).sub(controlsRef.current.target);

    const spherical = new THREE.Spherical();
    spherical.setFromVector3(offset);

    switch (direction) {
      case 'left':
        spherical.theta += angle;
        break;
      case 'right':
        spherical.theta -= angle;
        break;
      case 'up':
        spherical.phi -= angle;
        break;
      case 'down':
        spherical.phi += angle;
        break;
      default:
        break;
    }

    spherical.makeSafe();
    offset.setFromSpherical(spherical);
    camera.position.copy(controlsRef.current.target).add(offset);
    controlsRef.current.update();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!controlsRef.current) return;
      switch (e.key) {
        case 'ArrowLeft':
          handleRotate('left');
          break;
        case 'ArrowRight':
          handleRotate('right');
          break;
        case 'ArrowUp':
          handleRotate('up');
          break;
        case 'ArrowDown':
          handleRotate('down');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [controlsRef]);

  useEffect(() => {
    if (!controlsRef.current) return;
    const handleChange = () => {
      const distance = controlsRef.current.object.position.length();
      setZoomLevel(initialDist / distance);
    };
    const controls = controlsRef.current;
    controls.addEventListener('change', handleChange);
    return () => controls.removeEventListener('change', handleChange);
  }, [controlsRef, initialDist]);

  return (
    <div className="camera-controls">
      <div className="zoom-indicator">
        Zoom: {zoomLevel.toFixed(1)}x
      </div>

      <div className="control-card">
        <div className="control-label">Zoom</div>
        <div className="zoom-controls">
          <button className="control-btn" onClick={() => handleZoom('in')}>+</button>
          <button className="control-btn" onClick={() => handleZoom('out')}>-</button>
        </div>
      </div>

      <div className="control-card">
        <div className="control-label">Rotate</div>
        <div className="rotation-grid">
          <div />
          <button className="control-btn sm" onClick={() => handleRotate('up')}>↑</button>
          <div />
          <button className="control-btn sm" onClick={() => handleRotate('left')}>←</button>
          <button className="control-btn sm" onClick={() => handleRotate('down')}>↓</button>
          <button className="control-btn sm" onClick={() => handleRotate('right')}>→</button>
        </div>
      </div>
    </div>
  );
};

const BASE_PRICE = 1495;

const CONFIG_OPTIONS = {
  safety_rating: [
    { code: 'S0', label: 'Standard Edition', price: 0 },
    { code: 'S1', label: 'Advanced Shielded (AS-v1)', price: 285 },
    { code: 'S2', label: 'C-Grade Extreme (XP-X)', price: 640 }
  ],
  contact_media: [
    { code: 'M1', label: 'Armor-Core-SS', price: 0 },
    { code: 'M2', label: 'Titan-Alloy-C', price: 195 }
  ],
  probe_config: [
    { code: 'SR', label: 'Single Probe', price: 0 },
    { code: 'SC', label: 'Cable', price: 125 },
    { code: 'DR', label: 'Double Probe', price: 340 },
    { code: 'DC', label: 'Double Cable', price: 465 }
  ],
  thermal_profile: [
    { code: 'TP1', label: 'Precision Range', price: 0 },
    { code: 'TP2', label: 'High Heat', price: 175 },
    { code: 'TP3', label: 'Standard Environmental', price: 0 }
  ],
  mounting_interface: [
    { code: 'MI-A', label: 'Threaded Port 3/4', price: 0 },
    { code: 'MI-B', label: 'Threaded Port 1', price: 35 },
    { code: 'MI-C', label: 'Threaded Port 1.5', price: 55 },
    { code: 'MI-D', label: 'Flange Mount 150', price: 185 },
    { code: 'MI-E', label: 'Flange Mount 300', price: 285 }
  ],
  signal_protocol: [
    { code: 'P1', label: '2-Wire Sync', price: 0 },
    { code: 'P2', label: 'Dual-Channel Sync', price: 145 },
    { code: 'P3', label: 'Relay-Bridge v2', price: 225 }
  ],
  installation_path: [
    { code: 'H', label: 'Horizontal', price: 95 },
    { code: 'V', label: 'Vertical', price: 0 }
  ],
  enhancements: [
    { code: 'GPC', label: 'Gas Compensation', price: 185 },
    { code: 'CAL', label: 'Factory Cert', price: 95 },
    { code: 'PRO', label: 'Weather Shell', price: 75 },
    { code: 'TAG', label: 'Metal Ident Tag', price: 25 }
  ]
};

function ThreeDConfigurator() {
  const navigate = useNavigate();
  const [config, setConfig] = useState({
    safety_rating: 'S0',
    contact_media: 'M1',
    probe_config: '',
    thermal_profile: 'TP1',
    mounting_interface: 'MI-A',
    active_length: '10',
    active_length_2: '10',
    signal_protocol: 'P1',
    installation_path: 'V',
    selected_enhancements: []
  });

  const [partUrls, setPartUrls] = useState({});
  const [loading, setLoading] = useState(false);
  const [flashing, setFlashing] = useState(false);
  const [initialDist, setInitialDist] = useState(100);
  const controlsRef = useRef();
  const partBoxes = useRef({});
  const needsFit = useRef(true);


  const generateModelNumber = () => {
    const { safety_rating, contact_media, probe_config, thermal_profile, mounting_interface, active_length, active_length_2, signal_protocol, installation_path, selected_enhancements } = config;

    const isDouble = ['DR', 'DC'].includes(probe_config);
    const length1 = active_length || '0';
    const length2 = active_length_2 || '0';

    const segments = [
      'WM8K',
      probe_config || '____',
      installation_path,
      isDouble ? `${length1}ft-${length2}ft` : `${length1}ft`,
      safety_rating,
      contact_media,
      thermal_profile,
      mounting_interface,
      signal_protocol
    ];
    let base = segments.join('-');
    if (selected_enhancements.length > 0) {
      base += '-' + selected_enhancements.join('-');
    }
    return base;
  };

  const calculateTotal = () => {
    const priceFor = (group, code) => {
      const opt = CONFIG_OPTIONS[group].find(o => o.code === code);
      return opt ? opt.price : 0;
    };
    let total = BASE_PRICE;
    total += priceFor('safety_rating', config.safety_rating);
    total += priceFor('contact_media', config.contact_media);
    total += priceFor('probe_config', config.probe_config);
    total += priceFor('thermal_profile', config.thermal_profile);
    total += priceFor('mounting_interface', config.mounting_interface);
    total += priceFor('signal_protocol', config.signal_protocol);
    total += priceFor('installation_path', config.installation_path);
    config.selected_enhancements.forEach(code => {
      total += priceFor('enhancements', code);
    });
    // Length pricing: $25 per 3 ft per probe
    const lengthPrice = (ft) => Math.ceil(Math.max(parseFloat(ft) || 0, 0) / 3) * 25;
    total += lengthPrice(config.active_length);
    if (['DR', 'DC'].includes(config.probe_config)) {
      total += lengthPrice(config.active_length_2);
    }
    return total;
  };

  const modelNumber = generateModelNumber();

  const handleOptionChange = (key, value) => {
    setConfig(prev => {
      const newConfig = { ...prev, [key]: value };

      // Path logic: only selectable for 'SR' or 'HP'
      if (key === 'probe_config') {
        const isOrientable = ['SR', 'HP'].includes(value);
        if (!isOrientable) {
          newConfig.installation_path = 'V';
        } else if (value === 'HP') {
          newConfig.installation_path = 'H';
        }
      }

      return newConfig;
    });
  };

  const toggleSecondaryEnhancement = (code) => {
    setConfig(prev => {
      const opts = [...prev.selected_enhancements];
      const index = opts.indexOf(code);
      if (index === -1) opts.push(code);
      else opts.splice(index, 1);
      return { ...prev, selected_enhancements: opts };
    });
  };

  const handleModelLoaded = (name, boundingBox) => {
    if (!controlsRef.current) return;

    // Store this part's box
    partBoxes.current[name] = boundingBox;

    // Only active parts should be considered
    const activeMapping = getActiveParts();
    const activePartNames = Object.keys(activeMapping).filter(k => activeMapping[k]);

    // CHECK: Have all active parts loaded their boxes?
    const allLoaded = activePartNames.every(p => partBoxes.current[p]);
    if (!allLoaded) return; // Wait for the rest

    // Compute combined bounding box for ALL loaded parts
    const combinedBox = new THREE.Box3();
    activePartNames.forEach(p => {
      combinedBox.union(partBoxes.current[p]);
    });

    const center = new THREE.Vector3();
    combinedBox.getCenter(center);

    const size = new THREE.Vector3();
    combinedBox.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);

    const fov = 50;
    const distance = (maxDim / (2 * Math.tan(Math.PI * fov / 360))) * 1.5;

    const camera = controlsRef.current.object;

    // Update target to the center of the assembly
    controlsRef.current.target.copy(center);

    // Position camera relative to the center if it's a new assembly
    if (needsFit.current) {
      camera.position.set(center.x, center.y, center.z + distance);
      needsFit.current = false; // Reset after fitting
    } else {
      // Small adjustment if target shifted significantly
      const currentOffset = new THREE.Vector3().copy(camera.position).sub(controlsRef.current.target);
      if (currentOffset.length() < distance * 0.1 || currentOffset.length() > distance * 10) {
        camera.position.set(center.x, center.y, center.z + distance);
      }
    }

    camera.near = 0.1;
    camera.far = Math.max(distance * 100, 5000);
    camera.updateProjectionMatrix();
    controlsRef.current.update();

    setInitialDist(camera.position.distanceTo(center));
  };

  const getActiveParts = () => {
    const mapping = {
      main: true,
      single_probe: false,
      double_probe: false,
      cable: false,
      double_cable: false,
      horizontal_probe: false
    };

    const type = config.probe_config;
    const orient = config.installation_path;

    if (orient === 'H') {
      mapping.horizontal_probe = true;
    } else {
      if (type === 'SR') mapping.single_probe = true;
      else if (type === 'SC') mapping.cable = true;
      else if (type === 'DR') mapping.double_probe = true;
      else if (type === 'DC') mapping.double_cable = true;
      else if (type === 'HP') mapping.horizontal_probe = true;
    }

    return mapping;
  };

  const fetchModel = async () => {
    setLoading(true);
    needsFit.current = true;
    try {
      const activeMapping = getActiveParts();
      const activePartNames = Object.keys(activeMapping).filter(k => activeMapping[k]);

      const newUrls = { ...partUrls };

      // Cleanup old parts that are no longer selected
      Object.keys(newUrls).forEach(name => {
        if (!activeMapping[name]) {
          if (newUrls[name].startsWith('blob:')) URL.revokeObjectURL(newUrls[name]);
          delete newUrls[name];
          delete partBoxes.current[name];
        }
      });

      // Fetch new parts
      for (const part of activePartNames) {
        if (!newUrls[part]) {
          const res = await fetch(`https://industriquote.com/api/serve-3d-part?parts=${part}`);
          if (!res.ok) throw new Error(`Failed to fetch part: ${part}`);
          const blob = await res.blob();
          newUrls[part] = URL.createObjectURL(blob);
        }
      }

      setPartUrls(newUrls);
    } catch (err) {
      console.error(err);
      alert("Error generating model parts.");
    } finally {
      setLoading(false);
    }
  };

  // Flash effect when config changes
  useEffect(() => {
    setFlashing(true);
    const timer = setTimeout(() => setFlashing(false), 300);
    return () => clearTimeout(timer);
  }, [config]);

  // Initial fetch
  useEffect(() => {
    fetchModel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Automatic 3D update on specific changes
  useEffect(() => {
    fetchModel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.probe_config, config.installation_path]);

  const isOrientationEnabled = ['SR', 'HP'].includes(config.probe_config);

  return (
    <div className="App">
      <style>
        {`
          @keyframes flash-glow {
            0% { background: #fef0e0; transform: scale(1); }
            50% { background: #fde0b8; transform: scale(1.01); }
            100% { background: #fef0e0; transform: scale(1); }
          }
          .model-number-box.flash {
            animation: flash-glow 0.3s ease-out;
            border-color: #f47920 !important;
            box-shadow: 0 0 10px rgba(29, 78, 216, 0.15);
          }
        `}
      </style>
      <div className="sidebar">
        <button
          className="control-btn"
          style={{ width: '100%', padding: '0 15px', marginBottom: '1.5rem', fontSize: '0.75rem', background: '#6b7280', fontFamily: 'inherit' }}
          onClick={() => navigate('/')}
        >
          Back to Hub
        </button>
        <div style={{
          background: '#ffffff',
          paddingBottom: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          marginBottom: '1.5rem',
          position: 'sticky',
          top: 'var(--sidebar-sticky-top)',
          zIndex: 20
        }}>
          <div id="sidebar-title" style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.4rem',
            marginBottom: '0.35rem',
            paddingTop: '1.5rem'
          }}>
            <div style={{ flexShrink: 0, width: '18px', height: '18px', background: '#f47920', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '900', fontSize: '0.65rem' }}>IQ</div>
            <span style={{ fontSize: '0.62rem', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6b7280', flex: '1', minWidth: '200px' }}>IndustriQuote · WaveMaster Series</span>
          </div>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#111827', marginBottom: '0.75rem', letterSpacing: '-0.01em' }}>Sensor Configurator</h1>
          <div
            className={`model-number-box ${flashing ? 'flash' : ''}`}
            style={{
              background: '#fef0e0',
              border: '1px solid #f9c58d',
              padding: '0.85rem 1rem',
              borderRadius: '2px',
              fontSize: '0.8rem',
              fontFamily: 'JetBrains Mono, monospace',
              color: '#7c3d0a',
              fontWeight: '600',
              wordBreak: 'break-all',
              boxSizing: 'border-box',
              transition: 'all 0.3s ease'
            }}
          >
            {modelNumber}
          </div>
          {/* Price box */}
          <div style={{
            marginTop: '0.75rem',
            padding: '0.65rem 0.75rem',
            background: '#dbe6f5',
            border: '1px solid #a8c0dd',
            borderRadius: '2px',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#1a2e4a' }}>Your Organization's Price</span>
              <span style={{ fontSize: '1.2rem', fontWeight: '800', color: '#1a2e4a', fontVariantNumeric: 'tabular-nums' }}>
                ${(calculateTotal() * 0.75).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.3rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.68rem', color: '#6b7280', fontWeight: '500' }}>List price (–25%)</span>
              <span style={{ fontSize: '0.82rem', color: '#9ca3af', fontVariantNumeric: 'tabular-nums', textDecoration: 'line-through' }}>
                ${calculateTotal().toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* 1. Probe Config (Interactive) */}
          <ConfigSection
            label="Probe Config"
            value={config.probe_config}
            options={CONFIG_OPTIONS.probe_config}
            onChange={(val) => handleOptionChange('probe_config', val)}
            placeholder="Select Probe Configuration..."
          />

          {!config.probe_config ? (
            <div style={{
              background: '#f8fafc',
              border: '1px dashed #cbd5e1',
              borderRadius: '2px',
              padding: '2.5rem 1.5rem',
              textAlign: 'center',
              marginTop: '1rem'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: '#e2e8f0',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                color: '#64748b',
                fontSize: '1rem',
                fontWeight: '800'
              }}>!</div>
              <div style={{ fontSize: '0.65rem', color: '#1a2e4a', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: '800', marginBottom: '0.5rem' }}>
                Selection Required
              </div>
              <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0, lineHeight: '1.5', fontWeight: '500' }}>
                Please select a probe configuration to unlock model specifications and available options.
              </p>
            </div>
          ) : (
            <>
              {/* 2. Installation Path (Interactive) */}
              <div style={{ opacity: isOrientationEnabled ? 1 : 0.4, pointerEvents: isOrientationEnabled ? 'auto' : 'none' }}>
                <ConfigSection label="Installation Path (For Single Probe Configuration)" value={config.installation_path} options={CONFIG_OPTIONS.installation_path} onChange={(val) => handleOptionChange('installation_path', val)} />
              </div>

              {/* 3. Active Length (Interactive) */}
              <div className="config-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.25rem', marginBottom: '0.5rem' }}>
                  <div style={{ fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '700' }}>
                    {['DR', 'DC'].includes(config.probe_config) ? "Active Lengths (ft)" : "Active Length (ft)"}
                  </div>
                  <div style={{ fontSize: '0.65rem', color: '#d97706', fontWeight: '700', letterSpacing: '0.04em' }}>
                    $25 / 3 ft
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={config.active_length}
                      onChange={(e) => handleOptionChange('active_length', e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1').slice(0, 6))}
                      placeholder="Primary"
                      style={{
                        width: '100%',
                        padding: '0.7rem 0.8rem',
                        background: '#f9fafb',
                        border: '1px solid #d1d5db',
                        borderRadius: '2px',
                        color: '#111827',
                        fontSize: '0.88rem',
                        outline: 'none',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box'
                      }}
                    />
                    {['DR', 'DC'].includes(config.probe_config) && (
                      <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.65rem', color: '#9ca3af', fontWeight: '600', letterSpacing: '0.06em' }}>PROBE 1 · ft</span>
                    )}
                    {!['DR', 'DC'].includes(config.probe_config) && (
                      <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.65rem', color: '#9ca3af', fontWeight: '600', letterSpacing: '0.06em' }}>ft</span>
                    )}
                  </div>

                  {['DR', 'DC'].includes(config.probe_config) && (
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={config.active_length_2}
                        onChange={(e) => handleOptionChange('active_length_2', e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1').slice(0, 6))}
                        placeholder="Secondary"
                        style={{
                          width: '100%',
                          padding: '0.7rem 0.8rem',
                          background: '#f9fafb',
                          border: '1px solid #d1d5db',
                          borderRadius: '2px',
                          color: '#111827',
                          fontSize: '0.88rem',
                          outline: 'none',
                          fontFamily: 'inherit',
                          boxSizing: 'border-box'
                        }}
                      />
                      <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.65rem', color: '#9ca3af', fontWeight: '600', letterSpacing: '0.06em' }}>PROBE 2 · ft</span>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem', marginTop: '0.5rem' }}>
                <h3 style={{ fontSize: '0.62rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '1rem', fontWeight: '700' }}>Product Specifications</h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <ConfigSection label="Safety Rating" value={config.safety_rating} options={CONFIG_OPTIONS.safety_rating} onChange={(val) => handleOptionChange('safety_rating', val)} />
                  <ConfigSection label="Contact Media" value={config.contact_media} options={CONFIG_OPTIONS.contact_media} onChange={(val) => handleOptionChange('contact_media', val)} />
                  <ConfigSection label="Thermal Profile" value={config.thermal_profile} options={CONFIG_OPTIONS.thermal_profile} onChange={(val) => handleOptionChange('thermal_profile', val)} />
                  <ConfigSection label="Mounting Interface" value={config.mounting_interface} options={CONFIG_OPTIONS.mounting_interface} onChange={(val) => handleOptionChange('mounting_interface', val)} />
                  <ConfigSection label="Signal Protocol" value={config.signal_protocol} options={CONFIG_OPTIONS.signal_protocol} onChange={(val) => handleOptionChange('signal_protocol', val)} />

                  {/* Enhancements (Interactive) */}
                  <div className="config-section" style={{ marginBottom: '2rem' }}>
                    <div className="section-title" style={{ fontSize: '0.65rem', color: '#6b7280', marginBottom: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '700', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.25rem' }}>Enhancements</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                      {CONFIG_OPTIONS.enhancements.map(opt => {
                        const isSelected = config.selected_enhancements.includes(opt.code);
                        return (
                          <button
                            key={opt.code}
                            onClick={() => toggleSecondaryEnhancement(opt.code)}
                            style={{
                              padding: '0.6rem',
                              fontSize: '0.72rem',
                              borderRadius: '2px',
                              border: `1px solid ${isSelected ? '#1a2e4a' : '#d1d5db'}`,
                              background: isSelected ? '#dbe6f5' : '#f9fafb',
                              color: isSelected ? '#1a2e4a' : '#6b7280',
                              textAlign: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              fontWeight: isSelected ? '700' : '500',
                              fontFamily: 'inherit'
                            }}
                          >
                            <div>{opt.code}</div>
                            <div style={{ fontSize: '0.65rem', marginTop: '2px', color: isSelected ? '#f47920' : '#9ca3af', fontWeight: '600' }}>
                              +${opt.price.toLocaleString()}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Extra spacing at the bottom of the sidebar */}
        <div style={{ height: '4rem' }} />
      </div>

      <div className="canvas-container">
        {loading && <div className="loading-overlay">Updating 3D Geometry...</div>}

        <CameraOverlay
          controlsRef={controlsRef}
          onBack={() => navigate('/')}
          initialDist={initialDist}
        />

        <Canvas shadows dpr={[1, 1.5]} camera={{ position: [0, 0, 100], fov: 50 }}>
          <color attach="background" args={['#dde3ea']} />
          <ambientLight intensity={0.7} />
          <spotLight position={[0, 100, 200]} angle={0.25} penumbra={1} intensity={1.5} castShadow />
          <pointLight position={[-100, -100, -100]} intensity={1} />
          <directionalLight position={[0, 50, 0]} intensity={1} />

          <Suspense fallback={null}>
            <Environment preset="city" />
            {Object.keys(partUrls).map(name => (
              <ConfigModel
                key={name}
                name={name}
                url={partUrls[name]}
                color={PART_COLORS[name] || "#38bdf8"}
                onLoaded={handleModelLoaded}
              />
            ))}
            <ContactShadows key={Object.keys(partUrls).join(',')} frames={1} resolution={512} scale={100} blur={2.5} opacity={0.4} far={40} color="#000000" />
          </Suspense>

          <OrbitControls
            ref={controlsRef}
            makeDefault
            enablePan={true}
            enableZoom={true}
          />
        </Canvas>
      </div>
    </div>
  );
}

// Helper component for select sections
const ConfigSection = ({ label, value, options, onChange, disabled, placeholder }) => {
  const currentOpt = options.find(o => o.code === value);

  return (
    <div className="config-section" style={{ opacity: disabled ? 0.6 : 1 }}>
      <div className="section-title" style={{ fontSize: '0.65rem', color: '#6b7280', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '700', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.25rem' }}>
        {label}
      </div>

      {disabled ? (
        <div style={{
          width: '100%',
          padding: '0.7rem 0.8rem',
          background: '#f3f4f6',
          border: '1px solid #e5e7eb',
          borderRadius: '2px',
          color: '#9ca3af',
          fontSize: '0.88rem',
          fontStyle: 'italic',
          boxSizing: 'border-box'
        }}>
          {currentOpt ? `${currentOpt.code} - ${currentOpt.label}` : value}
        </div>
      ) : (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '100%',
            padding: '0.7rem 0.8rem',
            background: '#f9fafb',
            border: '1px solid #d1d5db',
            borderRadius: '2px',
            color: value ? '#111827' : '#9ca3af',
            fontSize: '0.88rem',
            outline: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
            boxSizing: 'border-box'
          }}
        >
          {placeholder && <option value="" disabled>{placeholder}</option>}
          {options.map(opt => (
            <option key={opt.code} value={opt.code}>
              {opt.code} — {opt.label}{opt.price > 0 ? ` (+$${opt.price.toLocaleString()})` : ''}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};

export default ThreeDConfigurator;
